import { useMemo } from 'react';

export type SubscriptionTier = 'free' | 'pro' | 'studio';

export interface FeatureAccess {
  logoUpload: boolean;
  qrCode: boolean;
  customTheme: boolean;
  currencySwitch: boolean;
  languageSwitch: boolean;
  unlimitedStyles: boolean;
  analytics: boolean;
  whiteLabel: boolean;
  multiArtist: boolean;
  centralAnalytics: boolean;
  teamManagement: boolean;
  whatsappLead: boolean;
  photoUpload: boolean;
  faqManagement: boolean;
  addressDirectory: boolean;
  centralStudioAddress: boolean;
  aiScanner: boolean;
  googleReviewSync: boolean;
  portfolioShowcase: boolean;
  verifiedBadge: boolean;
  referralSystem: boolean;
  sharedBranding: boolean;
  calculatorLayout: boolean;
}

const FEATURE_MAP: Record<SubscriptionTier, FeatureAccess> = {
  free: {
    logoUpload: false,
    qrCode: true,
    customTheme: false,
    currencySwitch: false,
    languageSwitch: false,
    unlimitedStyles: true,
    analytics: false,
    whiteLabel: false,
    multiArtist: false,
    centralAnalytics: false,
    teamManagement: false,
    whatsappLead: false,
    photoUpload: false,
    faqManagement: false,
    addressDirectory: false,
    centralStudioAddress: false,
    aiScanner: false,
    googleReviewSync: false,
    portfolioShowcase: false,
    verifiedBadge: false,
    referralSystem: false,
    sharedBranding: false,
    calculatorLayout: false,
  },
  pro: {
    logoUpload: true,
    qrCode: true,
    customTheme: true,
    currencySwitch: true,
    languageSwitch: true,
    unlimitedStyles: true,
    analytics: true,
    whiteLabel: true,
    multiArtist: false,
    centralAnalytics: false,
    teamManagement: false,
    whatsappLead: true,
    photoUpload: true,
    faqManagement: true,
    addressDirectory: true,
    centralStudioAddress: false,
    aiScanner: true,
    googleReviewSync: true,
    portfolioShowcase: true,
    verifiedBadge: true,
    referralSystem: true,
    sharedBranding: false,
    calculatorLayout: true,
  },
  studio: {
    logoUpload: true,
    qrCode: true,
    customTheme: true,
    currencySwitch: true,
    languageSwitch: true,
    unlimitedStyles: true,
    analytics: true,
    whiteLabel: true,
    multiArtist: true,
    centralAnalytics: true,
    teamManagement: true,
    whatsappLead: true,
    photoUpload: true,
    faqManagement: true,
    addressDirectory: true,
    centralStudioAddress: true,
    aiScanner: true,
    googleReviewSync: true,
    portfolioShowcase: true,
    verifiedBadge: true,
    referralSystem: true,
    sharedBranding: true,
    calculatorLayout: true,
  },
};

export function useSubscription(tier: SubscriptionTier = 'free') {
  const features = useMemo(() => FEATURE_MAP[tier], [tier]);
  const isPro = tier === 'pro' || tier === 'studio';
  const isStudio = tier === 'studio';

  return {
    tier,
    isPro,
    isStudio,
    features,
    canAccess: (feature: keyof FeatureAccess) => features[feature],
  };
}
